import {
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  HolderOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Button, Spin, Upload, UploadFile, UploadProps } from "antd";
import React, { DragEvent } from "react";
import { t } from "i18next";
import { classNames } from "src/common/classNames";
import { UploadInfo } from "src/components/PlmUploader/UploadInfo";
import styles from "./UploaderDragger.module.less";

const { Dragger } = Upload;

export interface PropsUploaderDragger {
  uploadProps: UploadProps;
  onDelete: (file: UploadFile, index: number) => void;
  onOrderChange?: (list: UploadFile[]) => void;
  maxSize?: number;
  isImage?: boolean;
}

export const UploaderDragger: React.FC<PropsUploaderDragger> = (props) => {
  const { uploadProps, maxSize, onOrderChange, onDelete, isImage } = props;
  const { fileList = [], maxCount, accept, disabled } = uploadProps;
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [dragList, setDragList] = React.useState<UploadFile[]>(fileList);

  React.useEffect(() => {
    setDragList(fileList);
  }, [fileList]);

  const onDragStart = (index: number) => () => {
    setDragIndex(index);
  };

  const onDragOver = (index: number) => (event: DragEvent<HTMLLIElement>) => {
    event.preventDefault();
    if (dragIndex === null) return;
    const dragOverItemIndex = index;
    if (dragIndex === dragOverItemIndex) {
      return;
    }
    const itemsCopy = [...fileList];
    const itemToMove = itemsCopy.splice(dragIndex, 1)[0];
    if (itemToMove) {
      itemsCopy.splice(dragOverItemIndex, 0, itemToMove);
      setDragList(itemsCopy);
    }
  };

  const onDragEnd = () => {
    setDragIndex(null);
    onOrderChange?.(dragList);
  };
  const actionsDisabled =
    fileList.some(({ status }) => status === "uploading") || disabled;

  const draggable =
    !fileList.find(
      ({ status: itemStatus }) =>
        itemStatus === "error" || itemStatus === "uploading",
    ) && !disabled;

  return (
    <div className={styles.draggerWrapper}>
      <Dragger
        className={classNames([
          styles.modalUpload,
          [(fileList?.length ?? 0) >= (maxCount ?? 1), styles.uploadDisabled],
        ])}
        {...uploadProps}
        listType="picture"
      >
        <div className={styles.dragger}>
          <FileOutlined className={styles.draggerIcon} />
          <div>
            {t(
              "Click or drag the file/s to the area to upload. Downloading from the clipboard is possible.",
            )}
          </div>
          <UploadInfo
            maxCount={maxCount ?? 1}
            accept={accept}
            maxSize={maxSize}
          />
        </div>
      </Dragger>
      <ul className={styles.fileList}>
        {dragList.map((file, index) => (
          <li
            key={file.uid}
            className={classNames([
              styles.listItemTitle,
              [file.status === "error", styles.listItemTitleErr],
              [!draggable, styles.listItemNotDraggable],
            ])}
            draggable={draggable}
            onDragStart={onDragStart(index)}
            onDragOver={onDragOver(index)}
            onDragEnd={onDragEnd}
          >
            <div className={styles.listItemInfo}>
              {draggable && <HolderOutlined />}
              {file.status === "done" && (
                <>
                  {isImage && (
                    <img
                      key={file.uid}
                      className={styles.listItemImg}
                      src={file.url ?? file.thumbUrl}
                      alt=""
                    />
                  )}
                  <div className={styles.listItemName}>{file.name}</div>
                </>
              )}
              {file.status === "error" && (
                <>
                  {isImage && (
                    <PictureOutlined
                      className={classNames([
                        styles.listItemInfoErr,
                        styles.infoErrImage,
                      ])}
                    />
                  )}
                  {!isImage && (
                    <FileOutlined className={styles.listItemInfoErr} />
                  )}
                  <div className={styles.listItemName}>{file.name}</div>
                </>
              )}
              {file.status === "uploading" && <Spin />}
            </div>
            <div className={styles.listItemControl}>
              {file.url && file.status === "done" && (
                <Button
                  icon={<DownloadOutlined />}
                  href={file.url}
                  disabled={actionsDisabled}
                  type="text"
                  download={file.name}
                />
              )}
              <Button
                icon={<DeleteOutlined />}
                onClick={() => {
                  onDelete(file, index);
                }}
                type="text"
                disabled={actionsDisabled}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
